import type { Product, ProductUnit } from '@/types/catalog'
import apiClient from '@/api/client'
import { getApiBaseUrl } from '@/api/base-url'

// Используем абсолютный URL
const apiUrl = getApiBaseUrl()

// Backend (FavoriteService::getUserFavorites) отдаёт snake_case-поля,
// а Product ждёт camelCase - без маппинга rating/stock/review_count теряются молча.
interface RawFavoriteProduct {
  id: number | string
  name: string
  sku?: string
  price?: unknown
  original_price?: unknown
  image_url?: string | null
  images?: string[]
  category_name?: string | null
  manufacturer_name?: string | null
  in_stock?: boolean
  stock_quantity?: number
  unit?: ProductUnit | null
  rating?: number
  review_count?: number
  short_description?: string | null
}

function mapFavoriteProduct(raw: RawFavoriteProduct): Product {
  const product: Product = {
    id: Number(raw.id),
    name: raw.name,
    sku: raw.sku ?? '',
    price: raw.price as Product['price'],
    categoryName: raw.category_name ?? '',
    manufacturerName: raw.manufacturer_name ?? '',
    shortDescription: raw.short_description ?? '',
    description: raw.short_description ?? '',
    images: raw.images ?? [],
    rating: raw.rating ?? 0,
    reviewCount: raw.review_count ?? 0,
    stockQuantity: raw.stock_quantity ?? 0,
    inStock: raw.in_stock ?? false,
    unit: raw.unit ?? null,
  }

  if (typeof raw.original_price === 'number') {
    product.originalPrice = raw.original_price
  }
  if (raw.image_url !== null && raw.image_url !== undefined) {
    product.imageUrl = raw.image_url
  }

  return product
}

export const favoritesApi = {
  async getFavorites(): Promise<Product[]> {
    const response = await apiClient.get(`${apiUrl}/api/favorites`)
    const data = response.data?.data || response.data || []
    return Array.isArray(data) ? (data as RawFavoriteProduct[]).map(mapFavoriteProduct) : []
  },

  async addToFavorites(product: Product): Promise<boolean> {
    const productId = String(product.id)
    const response = await apiClient.post(`${apiUrl}/api/favorites/${productId}`)
    return Boolean(response.data?.success)
  },

  async removeFromFavorites(productId: string | number): Promise<boolean> {
    const response = await apiClient.delete(`${apiUrl}/api/favorites/${productId}`)
    return Boolean(response.data?.success)
  },

  async toggleFavorite(product: Product): Promise<{ isFavorite: boolean }> {
    const response = await apiClient.post(`${apiUrl}/api/favorites/toggle`, {
      product_id: Number(product.id),
    })
    const data = response.data?.data ?? response.data
    return {
      isFavorite: Boolean(data?.is_favorite ?? data?.isFavorite),
    }
  },

  async isFavorite(productId: string | number): Promise<boolean> {
    const response = await apiClient.get(`${apiUrl}/api/favorites/check/${productId}`)
    const data = response.data?.data ?? response.data
    return Boolean(data?.is_favorite ?? data?.isFavorite)
  },

  async clearFavorites(): Promise<boolean> {
    const response = await apiClient.delete(`${apiUrl}/api/favorites/clear`)
    return Boolean(response.data?.success)
  },

  async getFavoritesCount(): Promise<number> {
    const response = await apiClient.get(`${apiUrl}/api/favorites/count`)
    const data = response.data?.data ?? response.data
    return Number(data?.count ?? 0)
  },
}
